export const BASE_URL = import.meta.env.VITE_SITE_URL || 'https://peternity.io';

export type SupportedLocale = 'en' | 'ru' | 'es';

export interface SlugEntry {
  slug: string;
  name: string;
}

/**
 * Maps page IDs to their localized slugs and display names.
 * Used for hreflang generation, breadcrumbs, and sitemap.
 */
export const SLUG_MAP: Record<string, Record<SupportedLocale, SlugEntry>> = {
  home: {
    en: { slug: '', name: 'Home' },
    ru: { slug: '', name: 'Главная' },
    es: { slug: '', name: 'Inicio' },
  },
  'ai-pet-companion': {
    en: { slug: 'ai-pet-companion', name: 'AI Pet Companion' },
    ru: { slug: 'ai-kompanion-pitomec', name: 'ИИ Компаньон' },
    es: { slug: 'companero-mascota-ia', name: 'Compañero IA' },
  },
  'pet-memorial': {
    en: { slug: 'pet-memorial', name: 'Pet Memorial' },
    ru: { slug: 'memorial-pitomca', name: 'Мемориал Питомца' },
    es: { slug: 'memorial-mascota', name: 'Memorial de Mascota' },
  },
  'digital-twin': {
    en: { slug: 'digital-twin', name: 'Digital Twin' },
    ru: { slug: 'cifrovoj-dvojnik', name: 'Цифровой Двойник' },
    es: { slug: 'gemelo-digital', name: 'Gemelo Digital' },
  },
  'pet-memories': {
    en: { slug: 'pet-memories', name: 'Pet Memories' },
    ru: { slug: 'vospominanija-o-pitomce', name: 'Воспоминания о Питомце' },
    es: { slug: 'recuerdos-mascota', name: 'Recuerdos de Mascota' },
  },
  pricing: {
    en: { slug: 'pricing', name: 'Pricing' },
    ru: { slug: 'ceny', name: 'Цены' },
    es: { slug: 'precios', name: 'Precios' },
  },
  about: {
    en: { slug: 'about', name: 'About' },
    ru: { slug: 'o-nas', name: 'О нас' },
    es: { slug: 'sobre-nosotros', name: 'О нас' },
  },
  blog: {
    en: { slug: 'blog', name: 'Blog' },
    ru: { slug: 'blog', name: 'Блог' },
    es: { slug: 'blog', name: 'Blog' },
  },
  'what-is-peternity': {
    en: { slug: 'what-is-peternity', name: 'What is Peternity?' },
    ru: { slug: 'chto-takoe-peternity', name: 'Что такое Peternity?' },
    es: { slug: 'que-es-peternity', name: '¿Qué es Peternity?' },
  },
  faq: {
    en: { slug: 'faq', name: 'FAQ' },
    ru: { slug: 'voprosy', name: 'Вопросы' },
    es: { slug: 'preguntas-frecuentes', name: 'Preguntas Frecuentes' },
  },
};

/** Locale prefix for URL construction. EN has no prefix. */
export const LOCALE_PREFIX: Record<SupportedLocale, string> = {
  en: '',
  ru: '/ru',
  es: '/es',
};

/**
 * Returns the full absolute URL for a page ID in a given locale.
 * Example: getPageUrl('ai-pet-companion', 'ru') → 'https://peternity.io/ru/ai-kompanion-pitomec'
 */
export function getPageUrl(pageId: string, locale: SupportedLocale): string {
  const entry = SLUG_MAP[pageId]?.[locale];
  if (!entry) return BASE_URL;
  const slug = entry.slug ? `/${entry.slug}` : '/';
  return `${BASE_URL}${LOCALE_PREFIX[locale]}${slug === '/' ? '' : slug}` || BASE_URL;
}

/**
 * Detects which locale a URL path belongs to.
 */
export function detectLocale(pathname: string): SupportedLocale {
  if (pathname.startsWith('/ru')) return 'ru';
  if (pathname.startsWith('/es')) return 'es';
  return 'en';
}

/**
 * Strips the locale prefix from a path.
 * '/ru/ai-kompanion-pitomec' → '/ai-kompanion-pitomec'
 */
export function stripLocalePrefix(pathname: string): string {
  return pathname.replace(/^\/(ru|es)/, '') || '/';
}

/**
 * Looks up a page ID by its localized slug.
 * '/ai-kompanion-pitomec' → 'ai-pet-companion'
 */
export function getPageIdBySlug(slug: string): string | null {
  const cleanSlug = slug.replace(/^\//, '');
  for (const [pageId, locales] of Object.entries(SLUG_MAP)) {
    for (const entry of Object.values(locales)) {
      if (entry.slug === cleanSlug) return pageId;
    }
  }
  return null;
}
